import { useState } from 'react';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar({ scrolled }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <div className="flex-shrink-0">
            <h1
              className={`text-xl sm:text-2xl font-display font-semibold tracking-tight transition-colors duration-500 ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              Alex Rivera
            </h1>
          </div>

          <div className="hidden md:flex items-center gap-8">
            {['about', 'collaborations', 'testimonials', 'contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item === 'collaborations' ? 'nyc-collabs' : item)}
                className={`text-sm font-sans font-medium capitalize transition-colors duration-300 cursor-pointer whitespace-nowrap hover:opacity-80 ${
                  scrolled ? 'text-gray-700 hover:text-sage-600' : 'text-white/90 hover:text-white'
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
                scrolled
                  ? 'text-gray-600 hover:text-sage-600 hover:bg-sage-50'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className="ri-instagram-line text-lg"></i>
            </a>
            <a
              href="https://tiktok.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
                scrolled
                  ? 'text-gray-600 hover:text-sage-600 hover:bg-sage-50'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className="ri-tiktok-line text-lg"></i>
            </a>
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
                scrolled
                  ? 'text-gray-600 hover:text-sage-600 hover:bg-sage-50'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className="ri-linkedin-line text-lg"></i>
            </a>
            <button
              onClick={() => scrollToSection('contact')}
              className={`ml-2 px-5 py-2 rounded-full text-sm font-display font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'bg-gradient-to-r from-sage-500 to-caramel-400 text-white hover:from-sage-600 hover:to-caramel-500'
                  : 'bg-white/15 backdrop-blur-sm text-white border border-white/30 hover:bg-white/25'
              }`}
            >
              Let&apos;s Talk
            </button>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-9 sm:w-10 h-9 sm:h-10 flex items-center justify-center cursor-pointer transition-colors duration-300 ${
              scrolled ? 'text-gray-900' : 'text-white'
            }`}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl sm:text-2xl`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-sage-100">
          <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-1">
            {['about', 'collaborations', 'testimonials', 'contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item === 'collaborations' ? 'nyc-collabs' : item)}
                className="block w-full text-left text-sm sm:text-base font-display font-medium text-gray-700 hover:text-sage-600 hover:bg-sage-50 py-2.5 sm:py-3 px-3 rounded-lg capitalize cursor-pointer whitespace-nowrap transition-colors"
              >
                {item}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-3 sm:pt-4 mt-2 border-t border-sage-100">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 sm:w-10 h-9 sm:h-10 flex items-center justify-center text-gray-600 hover:text-sage-600 hover:bg-sage-50 rounded-full cursor-pointer transition-colors"
              >
                <i className="ri-instagram-line text-lg sm:text-xl"></i>
              </a>
              <a
                href="https://tiktok.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 sm:w-10 h-9 sm:h-10 flex items-center justify-center text-gray-600 hover:text-sage-600 hover:bg-sage-50 rounded-full cursor-pointer transition-colors"
              >
                <i className="ri-tiktok-line text-lg sm:text-xl"></i>
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 sm:w-10 h-9 sm:h-10 flex items-center justify-center text-gray-600 hover:text-sage-600 hover:bg-sage-50 rounded-full cursor-pointer transition-colors"
              >
                <i className="ri-linkedin-line text-lg sm:text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
